import React from 'react';
import { Route, Switch } from 'react-router';
import { connect } from 'react-redux';
import _ from 'lodash';
import { getFormValues } from 'redux-form';

import { NOTIFICATION_DIALOG_MESSAGES } from 'Common/Constants';
import { TRANSACTION_FORM_NAME } from 'Common/FormEnum';
import {
  POS_ROUTES,
  SessionTypes,
  SymbolIndicatorEnum,
  QueueStatusEnum,
} from 'PointOfSale/modules/common/constants';
import { withRouter } from 'react-router-dom';

import * as POSSelectors from 'PointOfSale/selectors';
import { removeFromQueue } from 'PointOfSale/modules/queue/state/actions';
import { setSymbolsIsOpen } from 'PointOfSale/modules/individualServing/state/actions';
import { addNotificationDialogs } from 'Components/dialogs/state/actions';
import { updateCurrentTransaction } from 'PointOfSale/state/actions';

import ServingContainer from 'PointOfSale/modules/serving/container';
import IndividualServingContainer from 'PointOfSale/modules/individualServing/container';
import CEPContainer from 'PointOfSale/modules/cepCard/container/CEPContainer';
import GroupContainer from 'PointOfSale/modules/group/container/GroupContainer';
import TeacherContainer from 'PointOfSale/modules/teacher/container/TeacherContainer';

class Serving extends React.Component {
  componentDidUpdate(prevProps, prevState) {
    const { queueStatus: prevQueueStatus, profile: prevProfile } = prevProps;
    const {
      activeSession,
      history,
      location,
      writeTransactionError,
      hasMealCounterSession,
      queueStatus,
      profile,
      dispatch,
    } = this.props;

    const { location: prevLocation } = prevProps;

    const dialogs = [];

    const isQueuePage = location.pathname === POS_ROUTES.QUEUE;

    // On the queue page and the queue has been started, display
    // any allergens / special messages
    if (isQueuePage) {
      if (queueStatus === QueueStatusEnum.ON) {
        if (prevQueueStatus !== queueStatus || profile !== prevProfile) {
          /**
           * Since we're on the queue page, we'll need to do a page transition to individual serving
           * Delay the notification dialogs by 200ms, so that it makes it seem like the dialogs are coming from the individual serving
           */
          this.displayMessages(true);
        }
      }
    }

    if (
      // Display the allergens / special messages outside of the queue page
      !isQueuePage &&
      (profile !== prevProfile || prevLocation.pathname === POS_ROUTES.QUEUE)
    ) {
      this.displayMessages();
    }

    if (!prevProps.writeTransactionError && writeTransactionError) {
      if (
        activeSession === SessionTypes.Queue &&
        isQueuePage &&
        !hasMealCounterSession
      ) {
        history.replace(`${POS_ROUTES.QUEUE}/${profile.Id}`);
      }
      const message = writeTransactionError.Message;
      const dialog = {
        ...NOTIFICATION_DIALOG_MESSAGES.TRANSACTION_GENERIC_ERROR,
        message,
      };
      dialogs.push(dialog);
    }

    if (dialogs.length > 0) {
      dispatch(addNotificationDialogs(dialogs));
    }

    this.debouncedUpdateCurrentTransaction();
  }

  debouncedUpdateCurrentTransaction = _.debounce(
    () => this.props.dispatch(updateCurrentTransaction()),
    100
  );

  debouncedAddNotificationDialogs = _.debounce(
    (dialogs) => this.props.dispatch(addNotificationDialogs(dialogs)),
    200
  );

  /**
   * @param {Boolean} delayed - Determines whether to delay showing the notification dialogs
   */
  displayMessages = (delayed) => {
    const {
      activeSession,
      profile,
      history,
      showAllergenDialog,
      showSpecialMessageDialog,
      dispatch,
    } = this.props;
    const symbolIndicator = _.get(profile, 'symbolIndicator');

    if (!symbolIndicator) {
      return;
    }

    const dialogs = [];
    const symbols = [];
    //always show popups when offline

    const allergenIndex = symbolIndicator.findIndex(
      (x) => x.type === SymbolIndicatorEnum.Allergen
    );

    const specialMessageIndex = symbolIndicator.findIndex(
      (x) => x.type === SymbolIndicatorEnum.SpecialMessage
    );

    const invalidStudentIndex = symbolIndicator.findIndex(
      (x) => x.type === SymbolIndicatorEnum.InvalidStudent
    );

    const hasAllergen = allergenIndex > -1;
    const hasSpecialMessage = specialMessageIndex > -1;
    const hasInvalidStudent = invalidStudentIndex > -1;

    if (hasAllergen) {
      if (symbolIndicator[allergenIndex].isOpen && showAllergenDialog) {
        symbols.push({ isOpen: false, index: allergenIndex });
        const message = symbolIndicator[allergenIndex].message;
        const dialog = {
          ...NOTIFICATION_DIALOG_MESSAGES.STUDENT_HAS_ALLERGENS,
          message,
        };
        dialogs.push(dialog);
      }
    }

    if (hasSpecialMessage) {
      if (
        symbolIndicator[specialMessageIndex].isOpen &&
        showSpecialMessageDialog
      ) {
        symbols.push({ isOpen: false, index: specialMessageIndex });
        const message = symbolIndicator[specialMessageIndex].message;
        const dialog = {
          ...NOTIFICATION_DIALOG_MESSAGES.SPECIAL_MESSAGE,
          message,
        };
        dialogs.push(dialog);
      }
    }

    if (hasInvalidStudent) {
      if (symbolIndicator[invalidStudentIndex].isOpen) {
        const actionsToDispatchOnOk = [];
        symbols.push({ isOpen: false, index: invalidStudentIndex });

        if (SessionTypes.Queue === activeSession) {
          actionsToDispatchOnOk.push(removeFromQueue());
        }
        const dialog = {
          ...NOTIFICATION_DIALOG_MESSAGES.INVALID_STUDENT,
          actionsToDispatchOnOk,
        };
        dialogs.push(dialog);
      }
    }

    if (hasAllergen || hasSpecialMessage) {
      if (SessionTypes.Queue === activeSession) {
        history.replace(`${POS_ROUTES.QUEUE}/${profile.Id}`);
      }
    }

    if (symbols.length > 0) {
      dispatch(setSymbolsIsOpen(symbols));
    }

    if (dialogs.length > 0) {
      delayed
        ? this.debouncedAddNotificationDialogs(dialogs)
        : dispatch(addNotificationDialogs(dialogs));
    }
  };

  render() {
    return (
      <React.Fragment>
        <Switch>
          <Route
            exact
            path={POS_ROUTES.MEAL_COUNTER}
            component={CEPContainer}
          />
          <Route exact path={POS_ROUTES.QUEUE} component={ServingContainer} />
          <Route
            exact
            path={POS_ROUTES.QUEUE_INDIVIDUAL}
            component={IndividualServingContainer}
          />
          <Route exact path={POS_ROUTES.GROUP} component={GroupContainer} />
          <Route
            exact
            path={POS_ROUTES.GROUP_INDIVIDUAL}
            component={IndividualServingContainer}
          />
          <Route exact path={POS_ROUTES.TEACHER} component={TeacherContainer} />
          <Route
            exact
            path={POS_ROUTES.TEACHER_INDIVIDUAL}
            component={IndividualServingContainer}
          />
        </Switch>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  showAllergenDialog: POSSelectors.getShowAllergenDialog(state),
  showSpecialMessageDialog: POSSelectors.getShowSpecialMessageDialog(state),
  profile: POSSelectors.getProfile(state),
  writeTransactionError: POSSelectors.getWriteTransactionError(state),
  activeSession: POSSelectors.getActiveSession(state),
  hasMealCounterSession: POSSelectors.hasMealCounterSession(state),
  queueStatus: POSSelectors.getQueueStatus(state),
  transactionForm: getFormValues(TRANSACTION_FORM_NAME)(state),
});

export default withRouter(connect(mapStateToProps)(Serving));



// WEBPACK FOOTER //
// ./src/pages/pointofsale/modules/layouts/container/Serving.js